<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="title"
    @hide="onDialogHide"
    @submit="onSubmitForm"
  >
    <div class="row q-col-gutter-md">
      <p class="col-12 q-ma-none">
        {{ subTitle }}
      </p>
      <hr-list-select
        v-model="selectedTreatmentArea"
        :options="getTreatmentAreaOptions(null)"
        :label="$t('editProgram.textTreatmentArea')"
        :display-value="currentTreatmentAreaValue"
        :clearable="Boolean(currentTreatmentAreaValue)"
        :hide-selected="!Boolean(currentTreatmentAreaValue)"
        hide-bottom-space
        class="col-12"
        @clear="onClearTreatmentArea"
      />
      <hr-list-select
        v-if="subTreatmentAreas.length"
        v-model="selectedSubTreatmentArea"
        :options="subTreatmentAreas"
        :label="$t('settings.treatmentAreas.editTreatmentArea.subAreaDefault')"
        emit-object
        hide-bottom-space
        class="col-12"
      />
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        :label="submitButtonLabel"
      />
    </template>
  </hr-form-dialog>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { storeToRefs } from 'pinia';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrListSelect,
} from 'src/design-system';
import { auth as useAuthStore } from 'src/store/auth';
import { treatmentPlans } from 'src/store/treatmentPlans';
import { computed, ref } from 'vue';

const authStore = useAuthStore();
const { showDefaultTreatmentAreas } = storeToRefs(authStore);
const { clinicTreatmentAreas } = treatmentPlans();

defineOptions({
  name: 'TreatmentAreaSelectDialog',
});

defineProps({
  title: {
    type: String,
    required: true,
  },
  subTitle: {
    type: String,
    required: true,
  },
  submitButtonLabel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['hide', 'ok']);

const dialog = ref(null);
const selectedTreatmentArea = ref(null);
const selectedSubTreatmentArea = ref(null);

const subTreatmentAreas = computed(() => {
  if (!selectedTreatmentArea.value) {
    return [];
  }

  return clinicTreatmentAreas
    .filter((area) => area.parent === selectedTreatmentArea.value);
});

const show = () => {
  dialog.value.show();
};

const hide = () => {
  if (dialog.value) {
    dialog.value.hide();
  }
};

const onDialogHide = () => {
  emit('hide');
};

const onSubmitForm = () => {
  emit('ok', {
    selectedTreatmentArea: selectedTreatmentArea.value,
    selectedSubTreatmentArea: selectedSubTreatmentArea.value?.id || null,
  });
  hide();
};

const getTreatmentAreaOptions = (parent) => {
  if (!clinicTreatmentAreas.length) {
    return [{
      value: null,
      label: i18n.t('programs.noTreatmentAreasAvailable'),
    }];
  }

  return clinicTreatmentAreas.filter((area) => area
      && area.parent === parent
      && (
        !area.isDefaultArea
        || (showDefaultTreatmentAreas.value && area.isVisibleArea)
      ))
    .sort((a, b) => a.order - b.order)
    .map((area) => ({
      value: area.id,
      label: area.label,
    }));
};

const onClearTreatmentArea = () => {
  selectedTreatmentArea.value = null;
};

const getTreatmentAreaValue = ((id) => {
  return clinicTreatmentAreas.find((area) => area.id === id)?.label;
});

const currentTreatmentAreaValue = computed(() => {
  return getTreatmentAreaValue(selectedTreatmentArea.value);
});

defineExpose({ show, hide });
</script>
